import { SessionCache } from '@youversion/next-auth/client';
import { getCookies } from 'cookies-next';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';

import NextAuthConfiguration, { AUTH_FREE_PATHS } from '@/auth-config';
import dayjs from '@/lib/dayjs';
import { captureException } from '@/lib/sentry';
import { IS_STAGING } from '@/utils/env';
import { safeGetItem, safeRemoveItem, safeSetItem } from '@/utils/safe-storage';

const authFreePaths = new Set<string>(AUTH_FREE_PATHS);

const GEO_HOST = 'https://geo.life.church/where';

enum EventType {
  Heartbeat = 'com.youversion.portal.heartbeat.v1',
  PageView = 'com.youversion.portal.page_view.v1_1',
  SessionStart = 'com.youversion.portal.session_start.v1_1',
  UserActionError = 'com.youversion.portal.user_action_error.v1',
  UserActionStart = 'com.youversion.portal.user_action_start.v1',
  UserActionSucceed = 'com.youversion.portal.user_action_succeed.v1',
}

interface BaseCloudEventData {
  datacontenttype: 'application/json';
  id: string;
  source: string;
  specversion: '0.2'; // According to the docs, specversion should be 1.0.2 but it's not working
  subject: string;
  time: string;
  type: EventType;
}

interface BaseData {
  organization_id: string | null;
  session_id: string;
  reporting_lag: number;
  user_id?: number;
  browser_id: string;
  device: {
    brand_name?: string;
    operating_system?: string;
    language: string;
    browser?: string;
    browser_version?: string;
  };
  geo: {
    country: string | null;
    city: string | null;
    region: string | null;
  };
  app_info: {
    id: string;
    version: string;
  };
}

interface MetricsSchema<
  T extends EventType,
  // oxlint-disable-next-line typescript/no-explicit-any
  D extends Record<string, any> = Record<string, unknown>,
> extends BaseCloudEventData {
  data: BaseData & D;
  type: T;
}

interface HeartbeatData {
  interval: number;
}
type HeartbeatEvent = MetricsSchema<EventType.Heartbeat, HeartbeatData>;

interface PageViewData {
  name: string;
  url: string;
  path: string;
  title: string;
  referrer: string;
}
type PageViewEvent = MetricsSchema<EventType.PageView, PageViewData>;

interface SessionStartData {
  utm: {
    source: string | undefined;
    medium: string | undefined;
    campaign: string | undefined;
    term: string | undefined;
    content: string | undefined;
    ref: string | undefined;
  };
}
type SessionStartEvent = MetricsSchema<EventType.SessionStart, SessionStartData>;

export enum UserAction {
  CelebrationPageNavigationBanner = 'celebration_page_navigation_banner',
  CelebrationPageNavigationSidebar = 'celebration_page_navigation_sidebar',
  DownloadCelebrationImage = 'download_celebration_image',
  EventsEventCreate = 'events_event_create',
  EventsEventPublish = 'events_event_publish',
  OrganizationsChurchCreate = 'organizations_church_create',
  OrganizationsChurchPublish = 'organizations_church_publish',
}

interface UserActionData {
  action: UserAction;
  object_id?: string;
}

interface UserActionErrorData extends UserActionData {
  message: string;
}
type UserActionErrorEvent = MetricsSchema<EventType.UserActionError, UserActionErrorData>;
type UserActionStartEvent = MetricsSchema<EventType.UserActionStart, UserActionData>;
type UserActionSucceedEvent = MetricsSchema<EventType.UserActionSucceed, UserActionData>;

type MetricsEvent =
  | HeartbeatEvent
  | PageViewEvent
  | SessionStartEvent
  | UserActionErrorEvent
  | UserActionStartEvent
  | UserActionSucceedEvent;

const getStagingEvent = (event: EventType) => {
  const eventParts = event.split('.');
  eventParts[2] = `${eventParts[2]}_staging`;
  return eventParts.join('.');
};

const sendEvent = (event: MetricsEvent) => {
  const url = `https://${IS_STAGING ? 'staging.' : ''}metricsengine.io/topics/metrics-ingest/publish`;
  const body = JSON.stringify({
    ...event,
    type: IS_STAGING ? getStagingEvent(event.type) : event.type,
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, body);
  } else {
    fetch(url, { body, keepalive: true, method: 'POST' });
  }
};

/**
 * Resolve the current user id.
 *
 * On routes in the `(public)` group there is no `AuthProvider`, so calling
 * `SessionCache.getSession()` would fire a fresh `/authenticate` that always
 * 401s for anonymous visitors — we short-circuit instead. Everywhere else
 * (including `(auth)` and `(standalone)` routes that are listed in
 * `auth.config.publicPaths` but still mount `AuthProvider`),
 * `SessionCache.getSession()` resolves from cache or shares `AuthProvider`'s
 * in-flight promise, so logged-in users get a real `user_id`. Errors are
 * reported to Sentry and degrade to an anonymous event so a flaky session
 * endpoint doesn't drop the metric.
 */
const getUserId = async (): Promise<number | undefined> => {
  if (authFreePaths.has(window.location.pathname)) {
    return undefined;
  }
  try {
    const session = await SessionCache.getSession({ authApiRoute: NextAuthConfiguration.authApiRoute });
    return session?.user?.id;
  } catch (error) {
    captureException(error, { tags: { source: 'metrics.getUserId' } });
    return undefined;
  }
};

const getBaseCloudEventData = async (): Promise<Omit<BaseCloudEventData, 'type'>> => {
  const userId = await getUserId();
  return {
    datacontenttype: 'application/json',
    id: uuidv4(),
    source: window.location.origin,
    specversion: '0.2',
    subject: userId ? userId.toString() : '',
    time: dayjs().toISOString(),
  };
};

/**
 * Callers fire metrics without awaiting them, so a rejection here would surface as an
 * unhandled rejection and drop the event. A failed lookup or a corrupt cached value is
 * reported to Sentry and the event is sent without geo instead. A corrupt cached value is
 * cleared so it is refetched next time rather than failing every event.
 */
const getGeoData = async (): Promise<BaseData['geo']> => {
  const geoLocalStorage = safeGetItem('local', 'geo');
  try {
    if (geoLocalStorage) {
      return JSON.parse(geoLocalStorage);
    }
    const response = await fetch(GEO_HOST, { method: 'GET' });
    const json = await response.json();
    const { country_code = null, city = null, region = null } = json;
    const geo = {
      country: country_code,
      city,
      region,
    };
    safeSetItem('local', 'geo', JSON.stringify(geo));
    return geo;
  } catch (error) {
    captureException(error, { tags: { source: 'metrics.getGeoData' } });
    // A concurrent call may have cached good geo since this one read; only drop what this call read.
    if (geoLocalStorage && safeGetItem('local', 'geo') === geoLocalStorage) {
      safeRemoveItem('local', 'geo');
    }
    return { country: null, city: null, region: null };
  }
};

const getBaseData = async (reportingLag = 0): Promise<BaseData> => {
  let sessionId = safeGetItem('session', 'SESSION_ID');
  if (!sessionId) {
    sessionId = uuidv4();
    safeSetItem('session', 'SESSION_ID', sessionId);
  }

  let browserId = safeGetItem('local', 'BROWSER_ID');
  if (!browserId) {
    browserId = uuidv4();
    safeSetItem('local', 'BROWSER_ID', browserId);
  }

  const cookies = getCookies();
  const organizationId = cookies.organization_id;
  const appLocale = cookies.app_locale;

  const ua = new UAParser().getResult();
  const userId = await getUserId();
  const geo = await getGeoData();

  return {
    organization_id: organizationId ?? null,
    session_id: sessionId,
    reporting_lag: reportingLag,
    user_id: userId,
    browser_id: browserId,
    device: {
      brand_name: ua.device.vendor,
      operating_system: ua.os.name,
      language: appLocale || navigator.language,
      browser: ua.browser.name,
      browser_version: ua.browser.version,
    },
    geo,
    app_info: {
      id: 'portal',
      version: '1',
    },
  };
};

const getReportingLag = () => {
  return window.PAGE_LOAD_TIMESTAMP ? new Date().getTime() - window.PAGE_LOAD_TIMESTAMP.getTime() : 0;
};

const sendHeartbeatEvent = async (interval: number, reportingLag: number) => {
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData(reportingLag);
  const event: HeartbeatEvent = {
    ...baseCloudEventData,
    type: EventType.Heartbeat,
    data: {
      ...baseData,
      interval,
    },
  };
  sendEvent(event);
};

const startHeartbeat = () => {
  const reportingLag = getReportingLag();

  // 0 sec
  sendHeartbeatEvent(0, reportingLag);

  // 3 sec
  const delay3s = Math.max(0, 3000 - reportingLag);
  const offset3s = Math.max(0, reportingLag - 3000);
  const timeout3s = setTimeout(() => sendHeartbeatEvent(3, offset3s), delay3s);

  // 10 sec
  const delay10s = Math.max(0, 10000 - reportingLag);
  const offset10s = Math.max(0, reportingLag - 10000);
  const timeout10s = setTimeout(() => sendHeartbeatEvent(10, offset10s), delay10s);

  // 30 sec
  const delay30s = Math.max(0, 30000 - reportingLag);
  const offset30s = Math.max(0, reportingLag - 30000);
  const timeout30s = setTimeout(() => sendHeartbeatEvent(30, offset30s), delay30s);

  // 60 sec intervals
  const interval60s = setInterval(() => sendHeartbeatEvent(60, 0), 60000);

  // Cleanup timers
  return function clearHeartbeat() {
    clearTimeout(timeout3s);
    clearTimeout(timeout10s);
    clearTimeout(timeout30s);
    clearInterval(interval60s);
  };
};

const sendPageViewEvent = async (data: PageViewData) => {
  const reportingLag = getReportingLag();
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData(reportingLag);
  const event: PageViewEvent = {
    ...baseCloudEventData,
    type: EventType.PageView,
    data: {
      ...baseData,
      ...data,
    },
  };
  sendEvent(event);
};

const sendSessionStartEvent = async (query: Record<string, string | Array<string> | undefined> | URLSearchParams) => {
  // Do not send this event if there is already a session
  if (safeGetItem('session', 'SESSION_ID')) {
    return;
  }

  // Normalize to a simple getter that works for both URLSearchParams and plain objects.
  // Return undefined (not '') for missing keys so JSON.stringify strips them — matches legacy behavior.
  const get = (key: string): string | undefined =>
    query instanceof URLSearchParams ? (query.get(key) ?? undefined) : (query[key] as string | undefined);

  const reportingLag = getReportingLag();
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData(reportingLag);
  const sessionStartEvent: SessionStartEvent = {
    ...baseCloudEventData,
    type: EventType.SessionStart,
    data: {
      ...baseData,
      utm: {
        source: get('utm_source'),
        medium: get('utm_medium'),
        campaign: get('utm_campaign'),
        term: get('utm_term'),
        content: get('utm_content'),
        ref: get('utm_ref'),
      },
    },
  };
  sendEvent(sessionStartEvent);
};

const sendUserActionErrorEvent = async (data: UserActionErrorData) => {
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData();
  const userActionErrorEvent: UserActionErrorEvent = {
    ...baseCloudEventData,
    type: EventType.UserActionError,
    data: {
      ...baseData,
      ...data,
    },
  };
  sendEvent(userActionErrorEvent);
};

const sendUserActionStartEvent = async (data: UserActionData) => {
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData();
  const userActionStartEvent: UserActionStartEvent = {
    ...baseCloudEventData,
    type: EventType.UserActionStart,
    data: {
      ...baseData,
      ...data,
    },
  };
  sendEvent(userActionStartEvent);
};

const sendUserActionSucceedEvent = async (data: UserActionData) => {
  const baseCloudEventData = await getBaseCloudEventData();
  const baseData = await getBaseData();
  const userActionSucceedEvent: UserActionSucceedEvent = {
    ...baseCloudEventData,
    type: EventType.UserActionSucceed,
    data: {
      ...baseData,
      ...data,
    },
  };
  sendEvent(userActionSucceedEvent);
};

export const Metrics = {
  startHeartbeat,
  sendPageViewEvent,
  sendSessionStartEvent,
  sendUserActionErrorEvent,
  sendUserActionStartEvent,
  sendUserActionSucceedEvent,
};
